import React from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Input, Modal, toast, useWizard } from "@picklebet/ui";
import { parseError } from "@/utilities/errorsParser";
import { walletService } from "@/features/wallet/services/wallet";
import type { WithdrawStepName } from "@/features/wallet/components/withdraw/utils/registry";
import { WithdrawContent } from "../../blocks/withdraw-content/WithdrawContent";
import * as styles from "./BankDetailsStep.module.scss";

const schema = z.object({
  accountName: z
    .string()
    .regex(/^[A-Z0-9 ]{3,32}$/i, "3-32 alpha numeric characters required"),
  bsb: z.string().regex(/^[0-9]{6}$/, "BSB should be a valid 6 digit number"),
  accountNumber: z.string().regex(/^[0-9]{1,9}$/, "1-9 digit number required"),
});

export const BankDetailsStep = () => {
  const { goTo } = useWizard<WithdrawStepName>();

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = useForm({
    defaultValues: {
      accountName: "",
      bsb: "",
      accountNumber: "",
    },
    resolver: zodResolver(schema),
  });

  const onSubmit = async (values: z.infer<typeof schema>) => {
    const result = await walletService.addBank(
      values.accountName,
      values.bsb,
      values.accountNumber,
    );

    if (!result.ok) {
      toast({
        variant: "danger",
        title: "Couldn't add bank details",
        description: parseError(result.error),
      });
      return;
    }

    // the account they just added is the only way back to the amount form, so
    // there is nothing worth returning to here
    goTo("withdraw", { replace: true });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <WithdrawContent>
        <Input
          {...register("accountName")}
          placeholder={`Enter Name`}
          label={`Account Holder Name`}
          errorMessage={errors?.accountName?.message}
          noSpacing
        />
        <Input
          {...register("bsb")}
          placeholder={`Enter BSB Number`}
          label={`BSB`}
          inputMode={"numeric"}
          pattern="[0-9]*"
          errorMessage={errors?.bsb?.message}
          noSpacing
        />
        <Input
          {...register("accountNumber")}
          placeholder={`Enter Account Number`}
          label={`Account Number`}
          inputMode={"numeric"}
          pattern="[0-9]*"
          errorMessage={errors?.accountNumber?.message}
          noSpacing
        />

        <Modal.Footer>
          <Button
            className={styles.button}
            loading={isSubmitting}
            type={`submit`}
          >
            Add Bank Details
          </Button>
        </Modal.Footer>
      </WithdrawContent>
    </form>
  );
};
